import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useCompare } from "../context/CompareContext";
import { Scale } from "lucide-react";

const NavItem = ({ to, children, testid }) => (
    <NavLink
        to={to}
        data-testid={testid}
        className={({ isActive }) =>
            `label-overline px-3 py-2 transition-colors ${isActive ? "text-[#1E3A2B] border-b-2 border-[#1E3A2B]" : "text-[#4A5568] hover:text-[#0B192C]"}`
        }
    >
        {children}
    </NavLink>
);

const Header = () => {
    const { items } = useCompare();
    return (
        <header className="sticky top-0 z-40 bg-[#F8F7F4]/90 backdrop-blur-xl border-b border-[#0B192C]/10">
            <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
                <Link to="/" data-testid="logo-link" className="flex items-center gap-2">
                    <img
                        src="/logo-smartfinancett.png"
                        alt="SmartFinanceTT"
                        className="h-9 md:h-10 w-auto"
                    />
                    <span className="sr-only">SmartFinanceTT — TT Finance Compare</span>
                </Link>

                <nav className="hidden md:flex items-center gap-1">
                    <NavItem to="/products" testid="nav-products">Browse</NavItem>
                    <NavItem to="/products?type=loan" testid="nav-loans">Loans</NavItem>
                    <NavItem to="/products?type=credit_card" testid="nav-cards">Credit Cards</NavItem>
                    <NavItem to="/calculator" testid="nav-calculator">Calculator</NavItem>
                    <NavItem to="/mortgage-calculator" testid="nav-mortgage">Mortgage</NavItem>
                    <NavItem to="/admin" testid="nav-admin">Admin</NavItem>
                </nav>

                <Link
                    to="/compare"
                    data-testid="compare-tray-link"
                    className="flex items-center gap-2 px-4 py-2 bg-[#0B192C] text-[#F8F7F4] label-overline hover:bg-[#1E3A2B] transition-colors"
                >
                    <Scale className="w-4 h-4" strokeWidth={1.5} />
                    Compare
                    {items.length > 0 && (
                        <span className="bg-[#D1A77E] text-[#0B192C] px-1.5 text-xs">{items.length}</span>
                    )}
                </Link>
            </div>
        </header>
    );
};

const Footer = () => (
    <footer className="border-t border-[#0B192C]/10 mt-24 bg-[#EAE7E1]/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-14 grid md:grid-cols-12 gap-8">
            <div className="md:col-span-5">
                <img src="/logo-smartfinancett.png" alt="SmartFinanceTT" className="h-10 w-auto mb-4" />
                <p className="text-sm text-[#4A5568] max-w-md leading-relaxed">
                    Trinidad and Tobago&rsquo;s independent comparison platform for loans and credit cards.
                    We do not sell financial products — we surface them.
                </p>
                <div className="mt-5 flex items-center gap-2">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#2F855A] animate-pulse" />
                    <span className="label-overline text-[#718096]">Rates updated quarterly · TT$</span>
                </div>
            </div>
            <div className="md:col-span-3">
                <div className="label-overline text-[#0A4F49] mb-4">Compare</div>
                <ul className="space-y-2.5 text-sm">
                    <li><Link to="/products?category=personal" className="hover:text-[#0A4F49] transition-colors">Personal Loans</Link></li>
                    <li><Link to="/products?category=auto" className="hover:text-[#0A4F49] transition-colors">Auto Loans</Link></li>
                    <li><Link to="/products?category=mortgage" className="hover:text-[#0A4F49] transition-colors">Mortgages</Link></li>
                    <li><Link to="/products?category=business" className="hover:text-[#0A4F49] transition-colors">Business / SME</Link></li>
                    <li><Link to="/products?type=credit_card" className="hover:text-[#0A4F49] transition-colors">Credit Cards</Link></li>
                </ul>
            </div>
            <div className="md:col-span-2">
                <div className="label-overline text-[#0A4F49] mb-4">Tools</div>
                <ul className="space-y-2.5 text-sm">
                    <li><Link to="/calculator" className="hover:text-[#0A4F49] transition-colors">Loan Calculator</Link></li>
                    <li><Link to="/mortgage-calculator" className="hover:text-[#0A4F49] transition-colors">Mortgage Calculator</Link></li>
                    <li><Link to="/compare" className="hover:text-[#0A4F49] transition-colors">Side-by-Side</Link></li>
                    <li><Link to="/admin" className="hover:text-[#0A4F49] transition-colors">Admin</Link></li>
                </ul>
            </div>
            <div className="md:col-span-2">
                <div className="label-overline text-[#0A4F49] mb-4">Reach</div>
                <ul className="space-y-2.5 text-sm">
                    <li><a href="mailto:admin@smartfinancett.com" className="hover:text-[#0A4F49] transition-colors">Contact</a></li>
                    <li><span className="text-[#718096]">Port of Spain</span></li>
                    <li><span className="text-[#718096]">Trinidad &amp; Tobago</span></li>
                </ul>
            </div>
        </div>
        <div className="border-t border-[#0B192C]/10">
            <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6 flex flex-col md:flex-row gap-2 justify-between text-xs text-[#718096]">
                <span>© 2026 SmartFinanceTT. All rates indicative — verify with provider.</span>
                <span className="label-overline">smartfinancett.com</span>
            </div>
        </div>
    </footer>
);

const Layout = ({ children }) => {
    const location = useLocation();
    React.useEffect(() => {
        window.scrollTo(0, 0);
    }, [location.pathname]);

    return (
        <div className="min-h-screen flex flex-col">
            <Header />
            <main className="flex-1">{children}</main>
            <Footer />
        </div>
    );
};

export default Layout;
